import { PRODUCT_NAME } from "@staffsmith/web-auth";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useServices } from "../context.js";
import { ErrorNote } from "../ui.js";

// Sign in with an emailed one-time code, the portal's way in: staff set up each
// person's sign-in, and nobody has a password to remember. In dev, demo sign-ins
// may use a password instead (config.json allowPassword), while SES can mail
// only verified addresses.

type Mode =
  { kind: "email" } | { kind: "code"; email: string; session: string } | { kind: "password" };

export function SignInPage() {
  const { auth, allowPassword } = useServices();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>({ kind: "email" });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const attempt = async (step: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await step();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const switchTo = (next: Mode) => {
    setError(null);
    setMode(next);
  };

  return (
    <main className="sign-in">
      <h1>{PRODUCT_NAME}</h1>
      <p className="muted">Timesheets and shortlists for consultants and clients.</p>
      {mode.kind === "email" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const email = String(new FormData(e.currentTarget).get("email"));
            void attempt(async () => {
              const session = await auth.startEmailCode(email);
              setMode({ kind: "code", email, session });
            });
          }}
        >
          <label>
            Email
            <input name="email" type="email" autoComplete="username" required />
          </label>
          <ErrorNote error={error} />
          <button type="submit" disabled={busy}>
            {busy ? "Sending…" : "Email me a sign-in code"}
          </button>
          {allowPassword ? (
            <button type="button" className="link" onClick={() => switchTo({ kind: "password" })}>
              Demo sign-in with a password
            </button>
          ) : null}
        </form>
      ) : mode.kind === "code" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const code = String(new FormData(e.currentTarget).get("code")).trim();
            void attempt(async () => {
              await auth.confirmEmailCode(mode.email, mode.session, code);
              void navigate("/");
            });
          }}
        >
          <p className="muted">We emailed a code to {mode.email}. It expires in a few minutes.</p>
          <label>
            Code
            <input
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6,8}"
              required
            />
          </label>
          <ErrorNote error={error} />
          <button type="submit" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <button type="button" className="link" onClick={() => switchTo({ kind: "email" })}>
            Start again
          </button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const form = new FormData(e.currentTarget);
            void attempt(async () => {
              await auth.signInWithPassword(
                String(form.get("email")),
                String(form.get("password")),
              );
              void navigate("/");
            });
          }}
        >
          <label>
            Email
            <input name="email" type="email" autoComplete="username" required />
          </label>
          <label>
            Password
            <input name="password" type="password" autoComplete="current-password" required />
          </label>
          <ErrorNote error={error} />
          <button type="submit" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <button type="button" className="link" onClick={() => switchTo({ kind: "email" })}>
            Use an emailed code instead
          </button>
        </form>
      )}
    </main>
  );
}
